import css from "./Show.module.css";

import { Config } from "@planetscale/database";
import { and, eq, sql } from "drizzle-orm";
import { Link, redirect, useParams } from "react-router";
import { z } from "zod";
import {
  commentsWithObjects,
  getCommentObject,
  getCommentPreview,
} from "~/components/site/Comments";
import { formatDate } from "~/components/site/Date";
import { UserSidebar } from "~/components/site/user/UserSiderbar";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { Tabs } from "~/components/ui/Tabs";
import { pg } from "~/pg.server";
import { stripSlashes } from "~/util/common";
import { tt, useRealm, useT, useTurl } from "~/util/Lang";
import { PagerDB } from "~/util/Listing";
import {
  $LOADER,
  $PAGE,
  callApi,
  MetaArgs,
  OurLoaderArgs,
} from "~/util/Loaders";
import { homePage } from "~/util/titles";
import { unlinkDiscordApi } from "../api/auth/discordUnlink.pages";
import { logPageView } from "~/util/stats";

const PageQuery = z.object({
  p: z.optional(z.string()),
});

const Params = z.object({ userId: z.string() });

async function makeData(args: OurLoaderArgs, cfg: Config) {
  const t = tt.bind(null, args.realm.lang);
  const params = Params.parse(args.params);
  if (!params.userId) {
    return redirect("/");
  }

  const user = await pg.db
    .select({
      id: pg.users.id,
      login: pg.users.login,
      createdAt: pg.users.createdAt,
    })
    .from(pg.users)
    .where(eq(pg.users.id, parseInt(params.userId)))
    .then((res) => res.at(0));

  if (!user) {
    throw Response.json(
      { msg: t({ en: "User not found.", ru: "Пользователь не найдена." }) },
      { status: 404 }
    );
  }

  // Log
  logPageView(args);
  //

  const query = PageQuery.parse(
    Object.fromEntries(args.url.searchParams.entries())
  );

  const page = parseInt(query.p ?? "1");
  const limit = 10;
  const offset = (page - 1) * limit;
  const commentsPerPage = 15;

  const [commentsResult, totalResult] = await Promise.all([
    pg.db
      .select({
        id: pg.comments.id,
        content: pg.comments.content,
        objType: pg.comments.objType,
        objUname: pg.comments.objUname,
        createdAt: pg.comments.createdAt,
        page: sql<number>`FLOOR((ROW_NUMBER() OVER (PARTITION BY obj_type, obj_uname ORDER BY created_at DESC) - 1) / ${commentsPerPage}) + 1`.as(
          "page"
        ),
        lang: pg.comments.lang,
        game: pg.comments.game,
      })
      .from(pg.comments)
      .where(
        and(eq(pg.comments.userId, user.id), eq(pg.comments.isDeleted, false))
      )
      .orderBy(sql`created_at DESC`)
      .limit(limit)
      .offset(offset),

    // Получаем общее количество комментариев
    pg.db
      .select({ count: sql<number>`COUNT(*)` })
      .from(pg.comments)
      .where(
        and(eq(pg.comments.userId, user.id), eq(pg.comments.isDeleted, false))
      ),
  ]);

  const comments = commentsResult;
  const totalCount = totalResult[0]?.count || 0;

  return {
    authUser: args.req.sess?.user,
    user,
    comments: commentsWithObjects({ comments, args }),
    paging: { page, totalCount, pageSize: 10 },
  };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: string; canonicalPath?: string }
) {
  return {
    title: `${opts.title} - ${cx.t({
      en: "Users",
      ru: "Пользователи",
    })} - ${homePage(cx.realm)}`,

    ogTitle: `L2Hub user ${opts.title}`,
  };
}

$PAGE({
  path: "users/:userId/*",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) => {
    return makeMeta(cx, {
      title: cx.data.user.login,
      canonicalPath: `/users/${cx.data.user.id}`,
    });
  },
  render: ({ data }) => {
    const { authUser } = data;
    const t = useT();
    const turl = useTurl();
    const params = useParams();
    const realm = useRealm();
    const { user, comments } = data;
    const pageTitle = t({ en: user.login, ru: user.login });

    async function doUnlinkDiscrord() {
      if (!authUser) {
        return;
      }
      const result = await callApi(unlinkDiscordApi, {
        input: { userId: data.user.id },
        realm,
      });
      console.log(result);
    }

    return (
      <div>
        <div className="content">
          <Breadcrumb
            noRoot={true}
            items={[
              {
                title: t({ en: "Users", ru: "Пользователи" }),
                url: turl(`/users`),
              },
              { title: pageTitle, url: turl(`/users/${user.id}`) },
            ]}
          />
          <div className="columns">
            <div className="column is-three-quarters">
              <PageTitle>{pageTitle}</PageTitle>

              <Tabs
                current={params["*"] ?? ""}
                link={(id) => turl(stripSlashes(`/users/${user.id}/${id}`))}
                tabs={[
                  {
                    id: "comments",
                    title: t({ en: "Comments", ru: "Комментарии" }),
                    body: () => (
                      <>
                        <Table
                          data={comments}
                          id={(comment) => comment.id}
                          cols={[
                            {
                              width: 200,
                              title: t({ en: "Subject", ru: "Объект" }),
                              cell: (comment) => {
                                const { path, link } =
                                  getCommentObject(comment);
                                return (
                                  <Link
                                    className={css.link}
                                    to={
                                      path + `/comments#comment-${comment.id}`
                                    }
                                  >
                                    {link}
                                  </Link>
                                );
                              },
                            },
                            {
                              title: t({ en: "Preview", ru: "Комментарий" }),
                              cell: (comment) =>
                                getCommentPreview(comment.content),
                            },
                            {
                              width: 150,
                              title: t({ en: "Published", ru: "Опубликован" }),
                              cell: (comment) => formatDate(comment.createdAt),
                            },
                          ]}
                        />
                        <PagerDB paging={data.paging} tab="comments" />
                      </>
                    ),
                  },
                ]}
              />
            </div>
            <div className="column">
              <UserSidebar
                counters={{ comments: data.paging.totalCount }}
                user={user}
              />
              {authUser && authUser.isAdmin && (
                <div onClick={doUnlinkDiscrord}>Unlink Discord</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
});
